/**
 * Modèles de message approuvés.
 *
 * Un texte destiné à des dizaines de milliers de personnes doit avoir été relu.
 * Le circuit brouillon → relecture → approuvé n'est pas décoratif : seul un
 * modèle approuvé peut porter une campagne.
 *
 * L'écran insiste sur un point que le compteur de caractères ne dit pas :
 * la segmentation se calcule sur le message **rendu**. « Bonjour [prenom] »
 * n'a pas la même longueur pour Aline que pour Marie-Christine.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, type Modele, type StatutModele } from '../../api/client';
import {
  Bandeau,
  Carte,
  Chargement,
  EtatVide,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate } from '../../lib/format';

const ETATS: Record<StatutModele, { ton: 'ok' | 'attente' | 'succes' | 'echec'; libelle: string }> =
  {
    draft: { ton: 'attente', libelle: 'Brouillon' },
    pending: { ton: 'ok', libelle: 'En relecture' },
    approved: { ton: 'succes', libelle: 'Approuvé' },
    rejected: { ton: 'echec', libelle: 'Refusé' },
  };

/** Variables reconnues, pour guider la rédaction sans imposer un vocabulaire. */
const VARIABLES_USUELLES = ['prenom', 'montant', 'date', 'code', 'compte', 'agence'];

interface Brouillon {
  id?: string;
  name: string;
  body: string;
  category: 'transactional' | 'marketing';
}

const VIDE: Brouillon = { name: '', body: '', category: 'transactional' };

/** Variables d'un corps, calculées côté console pour un retour immédiat. */
const variablesDe = (corps: string) => [
  ...new Set([...corps.matchAll(/\[([a-z0-9_]+)\]/g)].map((m) => m[1]!)),
];

export function Modeles() {
  const [modeles, setModeles] = useState<Modele[] | null>(null);
  const [peutApprouver, setPeutApprouver] = useState(false);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [avis, setAvis] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data, can_approve } = await api.modeles();
      setModeles(data);
      setPeutApprouver(can_approve);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function enregistrer() {
    if (!brouillon) return;
    setErreur(null);
    setAvis(null);
    setEnCours(true);
    try {
      const charge = {
        name: brouillon.name.trim(),
        body: brouillon.body,
        category: brouillon.category,
      };
      if (brouillon.id) {
        const misAJour = await api.modifierModele(brouillon.id, charge);
        if (misAJour.reset_to_draft) {
          setAvis(
            'Le corps a changé : le modèle est redevenu brouillon. L’approbation portait sur un texte qui n’existe plus.',
          );
        }
      } else {
        await api.creerModele(charge);
      }
      setBrouillon(null);
      await charger();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  async function statuer(modele: Modele, decision: 'submit' | 'approve' | 'reject' | 'withdraw') {
    setErreur(null);
    setAvis(null);
    try {
      await api.statuerModele(modele.id, decision, motifs[modele.id]);
      await charger();
    } catch (e) {
      setErreur(e);
    }
  }

  if (modeles === null) return <Chargement quoi="Chargement des modèles…" />;

  const variablesBrouillon = brouillon ? variablesDe(brouillon.body) : [];

  return (
    <>
      <MessageErreur erreur={erreur} />
      {avis && <MessageSucces>{avis}</MessageSucces>}

      <Bandeau titre="Un texte destiné à un envoi de masse doit avoir été relu.">
        Seul un modèle <strong>approuvé</strong> peut porter une campagne. Un membre rédige et
        soumet ; un administrateur du compte approuve — on ne s’approuve pas soi-même, sinon la
        relecture n’existe pas. Modifier le corps d’un modèle approuvé le ramène en brouillon :
        l’approbation portait sur un texte qui n’existe plus.
      </Bandeau>

      {brouillon && (
        <div style={{ marginBottom: 16 }}>
          <Carte titre={brouillon.id ? 'Modifier le modèle' : 'Nouveau modèle'}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
              <label className="champ" style={{ flex: '1 1 240px' }}>
                <span>Nom</span>
                <input
                  type="text"
                  value={brouillon.name}
                  onChange={(e) => setBrouillon({ ...brouillon, name: e.target.value })}
                  placeholder="Rappel d’échéance"
                />
              </label>
              <label className="champ" style={{ flex: '1 1 240px' }}>
                <span>Nature</span>
                <select
                  value={brouillon.category}
                  onChange={(e) =>
                    setBrouillon({
                      ...brouillon,
                      category: e.target.value as 'transactional' | 'marketing',
                    })
                  }
                >
                  <option value="transactional">Transactionnel</option>
                  <option value="marketing">Publicitaire</option>
                </select>
              </label>
            </div>

            <label className="champ" style={{ marginBottom: 14 }}>
              <span>Texte du message</span>
              <textarea
                rows={5}
                value={brouillon.body}
                onChange={(e) => setBrouillon({ ...brouillon, body: e.target.value })}
                maxLength={1600}
                placeholder="Bonjour [prenom], votre échéance du [date] s’élève à [montant] FCFA. Répondez STOP pour ne plus recevoir ces rappels."
              />
              <div className="aide">
                Les variables s’écrivent entre crochets, en minuscules :{' '}
                {VARIABLES_USUELLES.map((v) => (
                  <span key={v} className="mono">
                    [{v}]{' '}
                  </span>
                ))}
                . <strong>[prenom]</strong> vient du prénom du contact, les autres de ses colonnes
                importées.
              </div>
            </label>

            {variablesBrouillon.length > 0 && (
              <div className="bandeau orange" style={{ marginBottom: 14 }}>
                <b>
                  Ce modèle contient des variables : le coût variera d’un destinataire à l’autre.
                </b>
                Variables détectées :{' '}
                {variablesBrouillon.map((v) => (
                  <span key={v} className="etiquette-sid" style={{ marginRight: 6 }}>
                    {v}
                  </span>
                ))}
                . La segmentation se calcule sur le message rendu — un prénom long peut faire
                basculer à deux segments. L’aperçu de campagne chiffre le pire cas.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="primaire"
                onClick={enregistrer}
                disabled={enCours || !brouillon.name.trim() || !brouillon.body.trim()}
              >
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button className="secondaire" onClick={() => setBrouillon(null)}>
                Annuler
              </button>
            </div>
          </Carte>
        </div>
      )}

      <Carte
        titre={`Modèles — ${modeles.length}`}
        action={
          !brouillon && (
            <button className="accent" onClick={() => setBrouillon({ ...VIDE })}>
              Nouveau modèle
            </button>
          )
        }
        sansCorps
      >
        {modeles.length === 0 ? (
          <EtatVide titre="Aucun modèle pour l’instant.">
            <div className="aide" style={{ marginTop: 0 }}>
              Un modèle fige un texte relu et approuvé, avec ses variables. Il évite qu’une campagne
              parte sur un brouillon.
            </div>
          </EtatVide>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {modeles.map((modele) => {
              const etat = ETATS[modele.status];
              return (
                <div
                  key={modele.id}
                  style={{ padding: '16px 18px', borderBottom: '1px solid var(--bordure)' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'baseline',
                      flexWrap: 'wrap',
                      marginBottom: 8,
                    }}
                  >
                    <strong>{modele.name}</strong>
                    <PuceLibre ton={etat.ton}>{etat.libelle}</PuceLibre>
                    <span style={{ color: 'var(--muet)', fontSize: 12.5 }}>
                      {modele.category === 'marketing' ? 'Publicitaire' : 'Transactionnel'} ·{' '}
                      {modele.template_segments} segment(s) hors variables
                      {modele.usage_count > 0 && ` · ${modele.usage_count} message(s) émis`}
                    </span>
                  </div>

                  <div
                    style={{
                      background: 'var(--fond)',
                      border: '1px solid var(--bordure)',
                      borderRadius: 8,
                      padding: '11px 13px',
                      fontSize: 13.5,
                      textWrap: 'pretty',
                      marginBottom: 10,
                    }}
                  >
                    {modele.body}
                  </div>

                  {modele.variables.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      {modele.variables.map((v) => (
                        <span key={v} className="etiquette-sid" style={{ marginRight: 6 }}>
                          {v}
                        </span>
                      ))}
                    </div>
                  )}

                  {modele.reason && (
                    <div className="bandeau rouge" style={{ marginBottom: 10, fontSize: 13 }}>
                      Refusé : {modele.reason}
                    </div>
                  )}

                  <div style={{ color: 'var(--muet)', fontSize: 12, marginBottom: 10 }}>
                    {modele.approved_at
                      ? `Approuvé par ${modele.approved_by} le ${formaterDate(modele.approved_at)}.`
                      : modele.submitted_at
                        ? `Soumis par ${modele.submitted_by} ${formaterDate(modele.submitted_at)}.`
                        : `Créé ${formaterDate(modele.created_at)}.`}
                  </div>

                  {modele.status === 'pending' && peutApprouver && (
                    <label className="champ" style={{ maxWidth: 480, marginBottom: 10 }}>
                      <span>Motif — obligatoire en cas de refus</span>
                      <input
                        type="text"
                        value={motifs[modele.id] ?? ''}
                        onChange={(e) => setMotifs({ ...motifs, [modele.id]: e.target.value })}
                        placeholder="Mention de retrait absente, ton inadapté…"
                      />
                    </label>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(modele.status === 'draft' || modele.status === 'rejected') && (
                      <button className="primaire petit" onClick={() => statuer(modele, 'submit')}>
                        Soumettre à relecture
                      </button>
                    )}
                    {modele.status === 'pending' && peutApprouver && (
                      <>
                        <button
                          className="primaire petit"
                          onClick={() => statuer(modele, 'approve')}
                        >
                          Approuver
                        </button>
                        <button
                          className="secondaire petit"
                          onClick={() => statuer(modele, 'reject')}
                          disabled={!motifs[modele.id]?.trim()}
                        >
                          Refuser
                        </button>
                      </>
                    )}
                    {modele.status === 'pending' && !peutApprouver && (
                      <button
                        className="secondaire petit"
                        onClick={() => statuer(modele, 'withdraw')}
                      >
                        Retirer de la relecture
                      </button>
                    )}
                    <button
                      className="secondaire petit"
                      onClick={() =>
                        setBrouillon({
                          id: modele.id,
                          name: modele.name,
                          body: modele.body,
                          category: modele.category,
                        })
                      }
                    >
                      Modifier
                    </button>
                    <button
                      className="secondaire petit"
                      onClick={async () => {
                        await api.supprimerModele(modele.id).catch(setErreur);
                        await charger();
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!peutApprouver && (
          <div className="corps" style={{ paddingTop: 12 }}>
            <div className="aide" style={{ marginTop: 0 }}>
              Votre rôle vous permet de rédiger et de soumettre, pas d’approuver. Un administrateur
              du compte relira vos propositions.
            </div>
          </div>
        )}
      </Carte>
    </>
  );
}
